import type { ReactNode } from "react";

interface StackProps {
  gap?: number;
  align?: "start" | "center" | "end" | "stretch";
  grow?: boolean;
  /** Let this Stack become shorter than its contents, so it can scroll
   *  internally instead of pushing its parent taller. Off by default because
   *  that's the flexbox rule: flex children refuse to shrink past their
   *  content height. Pair with `grow` for a bounded, scrollable pane. */
  shrinkable?: boolean;
  /** Element to render as. Defaults to "div". Use "span" when the Stack must
   *  live inside phrasing content (e.g. a stat value wrapped in an inline
   *  element), where a nested <div> would be invalid HTML. */
  as?: "div" | "span";
  /** data-testid passed through to the rendered element. */
  "data-testid"?: string | undefined;
  children: ReactNode;
}

const gapClasses: Record<number, string> = {
  0: "gap-0",
  0.5: "gap-0.5",
  1: "gap-1",
  // Row has had 1.5 all along; Stack skipping it was an oversight, not a rule.
  1.5: "gap-1.5",
  2: "gap-2",
  // Row has 2.5; Stack lacking it is the same oversight as 1.5 was.
  2.5: "gap-2.5",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
  10: "gap-10",
  // Screen-level separation — the gap between a page header and its content,
  // not spacing within a component.
  16: "gap-16",
};

const alignClasses: Record<string, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  stretch: "items-stretch",
};

export function Stack({
  gap = 4,
  align,
  grow,
  shrinkable,
  as = "div",
  "data-testid": dataTestid,
  children,
}: StackProps) {
  const classes = [
    "flex flex-col",
    gapClasses[gap] ?? "gap-4",
    align && alignClasses[align],
    grow && "flex-1",
    shrinkable && "min-h-0",
  ]
    .filter(Boolean)
    .join(" ");

  const Component = as;
  return (
    <Component
      className={classes}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
    >
      {children}
    </Component>
  );
}
